/**
 * Defines which injury types are valid for each body part
 * Based on the injury matrix requirements
 */

export const InjuryTypes = {
  FRACTURE: 'fracture',
  DISLOCATED: 'dislocated',
  SPRAIN: 'sprain',
  IMPACT_SHOCK: 'impactShock',
  CUTS_WOUNDS: 'cutsWounds',
  CONCUSSION: 'concussion',
  ACUTE_SICKNESS: 'acuteSickness',
  DEAD: 'dead',
  OTHER: 'other'
} as const

export type InjuryType = (typeof InjuryTypes)[keyof typeof InjuryTypes]

export const BodyParts = {
  // Head and neck region
  HEAD: 'head',
  FACE: 'face',
  THROAT: 'throat',
  NECK: 'neck',
  // Upper torso
  COLLARBONE: 'collarbone',
  SHOULDER: 'shoulder',
  BREAST: 'breast',
  // Upper extremities
  UPPER_ARM: 'arm',
  WRIST: 'wrist',
  HAND: 'hand',
  THUMB: 'finger',
  // Torso
  ABDOMEN: 'stomach',
  BACK: 'back',
  // Lower torso and pelvis
  PELVIS: 'pelvis',
  BUTTOCKS: 'buttock',
  HIP: 'hip',
  GENITALIA: 'genitalia',
  // Lower extremities
  THIGH: 'thigh',
  KNEE: 'knee',
  LOWER_LEG: 'calf',
  FOOT_ANKLE: 'ankleFoot'
} as const

export type BodyPart = (typeof BodyParts)[keyof typeof BodyParts]

const ALL_INJURY_TYPES = Object.values(InjuryTypes) as InjuryType[]

const CONDITION_INJURY_TYPES: readonly InjuryType[] = [
  InjuryTypes.DEAD,
  InjuryTypes.ACUTE_SICKNESS
]

const INJURY_MODE_DEFAULT_TYPES: readonly InjuryType[] =
  ALL_INJURY_TYPES.filter(
    injuryType => !CONDITION_INJURY_TYPES.includes(injuryType)
  )

/**
 * Mapping of body parts to allowed injury types
 * Based on the screenshot showing the injury matrix
 */
export const BODY_PART_INJURY_MAPPING = {
  [BodyParts.HEAD]: [
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.CONCUSSION,
    InjuryTypes.OTHER
  ],
  [BodyParts.FACE]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.THROAT]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.NECK]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.COLLARBONE]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.SHOULDER]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.SPRAIN,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.UPPER_ARM]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.WRIST]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.SPRAIN,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.HAND]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.SPRAIN,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.THUMB]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.SPRAIN,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.BREAST]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.ABDOMEN]: [
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.BACK]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.BUTTOCKS]: [
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.HIP]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.PELVIS]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.GENITALIA]: [
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.THIGH]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.KNEE]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.SPRAIN,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.LOWER_LEG]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ],
  [BodyParts.FOOT_ANKLE]: [
    InjuryTypes.FRACTURE,
    InjuryTypes.DISLOCATED,
    InjuryTypes.SPRAIN,
    InjuryTypes.IMPACT_SHOCK,
    InjuryTypes.CUTS_WOUNDS,
    InjuryTypes.OTHER
  ]
} as const satisfies Record<BodyPart, readonly InjuryType[]>

/**
 * Gets the allowed injury types for a given body part
 */
type AllowedInjuryInput = {
  bodyPart?: string
  mode?: 'injury' | 'condition'
}

export const getAllowedInjuryTypes = ({
  bodyPart,
  mode = 'injury'
}: AllowedInjuryInput): readonly InjuryType[] => {
  if (mode === 'condition') return CONDITION_INJURY_TYPES
  if (!bodyPart) return INJURY_MODE_DEFAULT_TYPES
  return (
    BODY_PART_INJURY_MAPPING[bodyPart as BodyPart] || INJURY_MODE_DEFAULT_TYPES
  )
}

/**
 * Checks if a specific injury type is allowed for a given body part
 */
export const isInjuryAllowedForBodyPart = (
  bodyPart: string | undefined,
  injuryType: string | undefined
): boolean => {
  if (!injuryType) return false
  return getAllowedInjuryTypes({ bodyPart }).includes(injuryType as InjuryType)
}
